import React, { useState, useEffect } from 'react';
import { useStore } from '../../stores/useStore';
import { EmptyState } from '../Common/EmptyState';
import { Schedule, ScheduleType } from '../../types';



interface DailyViewProps {
    onFeedbackClick?: (scheduleId: string) => void;
}

const SCHEDULE_TYPES: ScheduleType[] = ['설치', '수리', '정기점검', '견적', '기타'];

const TYPE_CONFIG: Record<string, { bg: string; text: string }> = {
    '설치': { bg: '#e3f2fd', text: '#1e3a5f' },
    '수리': { bg: '#ffebee', text: '#8b4513' },
    '정기점검': { bg: '#e8f5e9', text: '#5a7a6a' },
    '견적': { bg: '#f3e5f5', text: '#5a5a5a' },
    '기타': { bg: '#f5f5f5', text: '#5a5a5a' },
};

const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#5a5a5a',
    marginBottom: '4px',
    display: 'block',
    letterSpacing: '0.3px',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'white',
    border: '1px solid #c4b8a5',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
    boxSizing: 'border-box',
    color: '#2c2c2c',
};

const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%235a5a5a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '16px',
    paddingRight: '28px',
};

interface AddForm {
    type: ScheduleType;
    clientId: string;
    machineId: string;
    location: string;
    address: string;
    assignedTo: string[];
    memo: string;
}

const defaultForm = (): AddForm => ({
    type: SCHEDULE_TYPES[0],
    clientId: '',
    machineId: '',
    location: '',
    address: '',
    assignedTo: [],
    memo: '',
});

const formatDate = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
    const todayStr = new Date().toLocaleDateString('en-CA');
    return `${m}월 ${d}일 (${weekDays[date.getDay()]})${dateStr === todayStr ? ' — 오늘' : ''}`;
};

export const DailyView: React.FC<DailyViewProps> = ({ onFeedbackClick }) => {
    const {
        selectedDate,
        dailyMemos,
        clients,
        machines,
        profiles,
        getSchedulesByDate,
        setSelectedSchedule,
        setActiveTab,
        updateDailyMemo,
        updateSchedule,
        deleteSchedule,
        addSchedule,
    } = useStore();

    const schedulesList = getSchedulesByDate(selectedDate);
    const [memo, setMemo] = useState(dailyMemos[selectedDate] || '');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<AddForm>(defaultForm());

    useEffect(() => {
        setMemo(dailyMemos[selectedDate] || '');
    }, [selectedDate, dailyMemos]);

    const handleMemoSave = () => {
        updateDailyMemo(selectedDate, memo);
    };

    const handleFeedback = (e: React.MouseEvent, scheduleId: string) => {
        e.stopPropagation();
        setSelectedSchedule(scheduleId);
        if (onFeedbackClick) {
            onFeedbackClick(scheduleId);
        } else {
            setActiveTab('work');
        }
    };

    const handleClientChange = (clientId: string) => {
        const client = clients.find((c) => c.id === clientId);
        setForm((f) => ({
            ...f,
            clientId,
            location: client ? client.name : '',
            address: client ? client.contact.address : '',
            machineId: '',
        }));
    };

    const clientMachines = machines.filter((m) => m.clientId === form.clientId);

    const openEditModal = (schedule: Schedule) => {
        setEditingId(schedule.id);
        setForm({
            type: schedule.type,
            clientId: schedule.clientId || '',
            machineId: schedule.machineId || '',
            location: schedule.location,
            address: schedule.address || '',
            assignedTo: schedule.assignedTo,
            memo: schedule.memo,
        });
        setShowAddModal(true);
    };

    const handleDeleteSchedule = (id: string) => {
        if (!window.confirm('이 작업을 삭제하시겠습니까?')) return;
        deleteSchedule(id);
    };

    const handleAddSubmit = () => {
        const location = form.location.trim();
        const memo = form.memo.trim();
        if (!location || !memo) return;

        const assignedTo = form.assignedTo;

        if (editingId) {
            updateSchedule(editingId, {
                type: form.type,
                clientId: form.clientId,
                machineId: form.machineId || undefined,
                location,
                address: form.address.trim(),
                timeSlot: { start: '', end: '' },
                memo,
                assignedTo,
            });
        } else {
            const newSchedule: Schedule = {
                id: `s_${Date.now()}`,
                date: selectedDate,
                timeSlot: { start: '', end: '' },
                type: form.type,
                status: '예정',
                clientId: form.clientId,
                ...(form.machineId ? { machineId: form.machineId } : {}),
                location,
                address: form.address.trim(),
                memo,
                assignedTo,
                createdAt: new Date().toISOString(),
            };
            addSchedule(newSchedule);
        }

        setForm(defaultForm());
        setEditingId(null);
        setShowAddModal(false);
    };

    const canSubmit = form.location.trim().length > 0 && form.memo.trim().length > 0;

    return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 날짜 헤더 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e3a5f' }}>
                    {formatDate(selectedDate)}
                </h2>
                <button
                    onClick={() => { setForm(defaultForm()); setShowAddModal(true); }}
                    style={{
                        backgroundColor: '#1e3a5f',
                        color: 'white',
                        padding: '8px 14px',
                        borderRadius: '10px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}
                >
                    + 작업 추가
                </button>
            </div>

            {/* 일자 메모 */}
            <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', borderRadius: '12px', padding: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#5a5a5a', marginBottom: '6px', display: 'block' }}>
                    📌 일자 메모
                </label>
                <textarea
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    onBlur={handleMemoSave}
                    placeholder="오늘의 특이사항을 입력하세요..."
                    style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#f4f1ea',
                        border: '1px solid #c4b8a5',
                        borderRadius: '10px',
                        fontSize: '14px',
                        resize: 'none',
                        height: '80px',
                        outline: 'none',
                        fontFamily: "'Noto Sans KR', sans-serif",
                        boxSizing: 'border-box',
                    }}
                />
            </div>

            {/* 작업 목록 */}
            <div>
                <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#5a5a5a', marginBottom: '8px' }}>
                    작업 {schedulesList.length}건
                </h3>

                {schedulesList.length === 0 ? (
                    <EmptyState
                        message="등록된 작업이 없습니다"
                        subMessage="새 작업을 추가해보세요"
                    />
                ) : (
                    <div style={{ border: '1px solid #c4b8a5', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden' }}>
                        {schedulesList.map((schedule, index) => {
                            const typeColor = TYPE_CONFIG[schedule.type] || TYPE_CONFIG['기타'];
                            const isLast = index === schedulesList.length - 1;

                            return (
                                <div
                                    key={schedule.id}
                                    style={{
                                        padding: '14px 16px',
                                        borderBottom: isLast ? 'none' : '1px solid #e8e0d5',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                        <span style={{
                                            fontSize: '10px',
                                            padding: '3px 8px',
                                            backgroundColor: typeColor.bg,
                                            color: typeColor.text,
                                            fontWeight: 700,
                                            borderRadius: '10px',
                                        }}>
                                            {schedule.type}
                                        </span>
                                    </div>

                                    <h4 style={{ fontWeight: 700, color: '#2c2c2c', marginBottom: '4px', fontSize: '15px' }}>
                                        {schedule.memo}
                                    </h4>

                                    <div style={{ fontSize: '13px', color: '#666' }}>
                                        <span>{schedule.location}</span>
                                    </div>

                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e8e0d5', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {schedule.status !== '완료' && schedule.status !== '취소' && (
                                            <button
                                                onClick={(e) => handleFeedback(e, schedule.id)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: '#1e3a5f',
                                                    color: 'white',
                                                    padding: '10px',
                                                    borderRadius: '10px',
                                                    border: 'none',
                                                    fontSize: '13px',
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                작업 완료 및 피드백
                                            </button>
                                        )}
                                        {schedule.status === '완료' && (
                                            <span style={{ fontSize: '12px', color: '#5a7a6a', fontWeight: 700 }}>
                                                ✓ 피드백 완료
                                            </span>
                                        )}
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); openEditModal(schedule); }}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #c4b8a5',
                                                    borderRadius: '10px',
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    color: '#5a5a5a',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                수정
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(schedule.id); }}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #c75c2e',
                                                    borderRadius: '10px',
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    color: '#c75c2e',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 작업 추가/수정 모달 */}
            {showAddModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                    onClick={() => { setShowAddModal(false); setEditingId(null); }}
                >
                    <div
                        style={{
                            backgroundColor: '#f4f1ea',
                            borderTop: '2px solid #c75c2e',
                            borderRadius: '20px 20px 0 0',
                            width: '100%',
                            maxHeight: '88vh',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            backgroundColor: '#1e3a5f',
                            color: 'white',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>
                                {editingId ? '작업 수정' : `작업 추가 — ${selectedDate}`}
                            </span>
                            <button
                                onClick={() => { setShowAddModal(false); setEditingId(null); }}
                                style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={labelStyle}>작업 유형</label>
                                <select
                                    value={form.type}
                                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as ScheduleType }))}
                                    style={selectStyle}
                                >
                                    {SCHEDULE_TYPES.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            {clients.length > 0 && (
                                <div>
                                    <label style={labelStyle}>거래처 선택 (선택사항)</label>
                                    <select
                                        value={form.clientId}
                                        onChange={(e) => handleClientChange(e.target.value)}
                                        style={selectStyle}
                                    >
                                        <option value="">— 직접 입력 —</option>
                                        {clients.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label style={labelStyle}>현장명 *</label>
                                <input
                                    value={form.location}
                                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                                    placeholder="예: 강남 빌딩"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={labelStyle}>주소</label>
                                <input
                                    value={form.address}
                                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                                    placeholder="예: 서울특별시 강남구 ..."
                                    style={inputStyle}
                                />
                            </div>

                            {form.clientId && clientMachines.length > 0 && (
                                <div>
                                    <label style={labelStyle}>기계 선택 (선택사항)</label>
                                    <select
                                        value={form.machineId}
                                        onChange={(e) => setForm((f) => ({ ...f, machineId: e.target.value }))}
                                        style={selectStyle}
                                    >
                                        <option value="">— 없음 —</option>
                                        {clientMachines.map((m) => (
                                            <option key={m.id} value={m.id}>{m.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {profiles.length > 0 && (
                                <div>
                                    <label style={labelStyle}>작업자</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {profiles.map((p) => {
                                            const active = form.assignedTo.includes(p.name);
                                            return (
                                                <button
                                                    key={p.id}
                                                    type="button"
                                                    onClick={() => setForm((f) => ({
                                                        ...f,
                                                        assignedTo: active
                                                            ? f.assignedTo.filter((n) => n !== p.name)
                                                            : [...f.assignedTo, p.name],
                                                    }))}
                                                    style={{
                                                        padding: '8px 14px',
                                                        border: active ? '2px solid #1e3a5f' : '1px solid #c4b8a5',
                                                        borderRadius: '20px',
                                                        backgroundColor: active ? '#1e3a5f' : 'white',
                                                        color: active ? 'white' : '#5a5a5a',
                                                        fontSize: '13px',
                                                        fontWeight: active ? 700 : 400,
                                                        cursor: 'pointer',
                                                        fontFamily: "'Noto Sans KR', sans-serif",
                                                    }}
                                                >
                                                    {p.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label style={labelStyle}>작업 내용 *</label>
                                <textarea
                                    value={form.memo}
                                    onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
                                    placeholder="작업 내용을 입력하세요"
                                    style={{ ...inputStyle, height: '80px', resize: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px', paddingBottom: '8px' }}>
                                <button
                                    onClick={() => { setShowAddModal(false); setEditingId(null); }}
                                    style={{
                                        flex: 1, padding: '13px', border: '1px solid #c4b8a5',
                                        backgroundColor: 'white', fontSize: '14px', fontWeight: 700,
                                        cursor: 'pointer', borderRadius: '10px', color: '#5a5a5a',
                                    }}
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleAddSubmit}
                                    disabled={!canSubmit}
                                    style={{
                                        flex: 2, padding: '13px', border: 'none',
                                        backgroundColor: canSubmit ? '#c75c2e' : '#c4b8a5',
                                        color: 'white', fontSize: '14px', fontWeight: 700,
                                        cursor: canSubmit ? 'pointer' : 'not-allowed', borderRadius: '10px',
                                    }}
                                >
                                    {editingId ? '수정 완료' : '작업 추가'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
